/**
 * LocalServerContent - Deployment content for local server option
 *
 * Shows instructions for:
 * - Installing the inference server locally
 * - Running code to connect to the local server
 */

import React from "react";
import { MarkdownPreview } from "../../../../MarkdownPreview";
import { Disclosure } from "@headlessui/react";
import { twClassNames } from "../../../../../helpers/twClassNames";
import Button from "../../../../Button";
import { Chip } from "../../../../Chip";
import {
    NumberedStep,
    BodyText,
    LanguageSelector,
    LocalInferenceInstallationStep
} from "../../DeployShared";
import {
    LanguageOption,
    WorkflowParameter,
    WorkflowImage,
    API_KEY_PLACEHOLDER,
    generateLocalCode,
    generateLocalLocalCode,
    replaceApiKeyPlaceholder
} from "../../DeployCodeGenerators";

export interface LocalServerContentProps {
    workflowUrl: string;
    workspaceUrl: string;
    apiKey: string | null;
    workflowParameters: WorkflowParameter[];
    workflowImages: WorkflowImage[];
    localDeployUrl: string;
    selectedLanguage: LanguageOption;
    onLanguageSelect: (language: LanguageOption) => void;
    isLocalEmbedded: boolean;
}

export function LocalServerContent({
    workflowUrl,
    workspaceUrl,
    apiKey,
    workflowParameters,
    workflowImages,
    localDeployUrl,
    selectedLanguage,
    onLanguageSelect,
    isLocalEmbedded
}: LocalServerContentProps) {
    return (
        <div className="space-y-4 p-2">
            <div className="flex items-center justify-between gap-2">
                <div className="space-y-2">
                    <h3 className="text-lg font-semibold text-gray-900">
                        Run on Your Server or Computer
                    </h3>
                </div>
            </div>

            <div className="w-full" data-color-mode="light">
                {isLocalEmbedded ? (
                    <div className="overflow-x-auto rounded-lg border border-gray-200">
                        <MarkdownPreview
                            source={generateLocalLocalCode(
                                workflowUrl,
                                workspaceUrl,
                                API_KEY_PLACEHOLDER,
                                workflowParameters,
                                workflowImages,
                                localDeployUrl
                            )}
                            onCopy={(text) => replaceApiKeyPlaceholder(text, apiKey)}
                        />
                    </div>
                ) : (
                    <>
                        <BodyText>
                            Process your workflow directly on your computer without sending data to
                            the cloud.
                        </BodyText>
                        <div className="flex flex-col gap-2">
                            <LocalInferenceInstallationStep />
                            <NumberedStep number={2} title="Connect Your Code" collapsible={true}>
                                <BodyText>
                                    Once the server is running, open a new tab in your terminal, and
                                    paste this code to send images to your workflow.
                                </BodyText>
                                <div className="my-4 flex-none">
                                    <LanguageSelector
                                        selectedLanguage={selectedLanguage}
                                        onLanguageSelect={onLanguageSelect}
                                    />
                                </div>
                                {(selectedLanguage.value === "javascript" ||
                                    selectedLanguage.value === "curl") && (
                                    <BodyText>
                                        You can pass input images as type <code>url</code> or{" "}
                                        <code>base64</code>.
                                    </BodyText>
                                )}
                                <div className="overflow-x-auto rounded-lg border border-gray-200">
                                    <MarkdownPreview
                                        source={generateLocalCode(
                                            workflowUrl,
                                            workspaceUrl,
                                            API_KEY_PLACEHOLDER,
                                            workflowParameters,
                                            workflowImages,
                                            localDeployUrl,
                                            selectedLanguage.value
                                        )}
                                        onCopy={(text) => replaceApiKeyPlaceholder(text, apiKey)}
                                    />
                                </div>
                            </NumberedStep>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
